import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MessageSquare, Save, Eye, EyeOff, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const SMSSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testPhone, setTestPhone] = useState('');
  const [settings, setSettings] = useState({
    sms_provider: 'twilio',
    // Twilio
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    // MSG91
    msg91_auth_key: '',
    msg91_sender_id: '',
    msg91_route: '4',
    // TextLocal
    textlocal_api_key: '',
    textlocal_sender: '',
    // Fast2SMS
    fast2sms_api_key: '',
    fast2sms_sender_id: '',
    // Common
    is_enabled: false,
    enable_fee_reminder: true,
    enable_admission_notification: true,
    enable_attendance_alert: true
  });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/settings/sms');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await apiClient.post('/api/settings/sms', settings);
      setMessage({ type: 'success', text: 'SMS settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: 'Please enter a phone number to test' });
      return;
    }
    setTesting(true);
    setMessage({ type: '', text: '' });
    try {
      await apiClient.post('/api/settings/sms/test', { phone: testPhone });
      setMessage({ type: 'success', text: 'Test SMS sent successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send test SMS. Check your configuration.' });
    } finally {
      setTesting(false);
    }
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderKeyInput = (label, name, placeholder) => (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type={showKeys[name] ? 'text' : 'password'}
          value={settings[name] || ''}
          onChange={(e) => setSettings({ ...settings, [name]: e.target.value })}
          placeholder={placeholder}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => toggleShowKey(name)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showKeys[name] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">SMS Gateway Settings</h1>
        <p className="text-gray-600 mt-1">Configure SMS gateway for notifications and alerts</p>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            SMS Provider Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label>SMS Provider</Label>
            <Select value={settings.sms_provider} onValueChange={(v) => setSettings({ ...settings, sms_provider: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="twilio">Twilio (International)</SelectItem>
                <SelectItem value="msg91">MSG91 (India)</SelectItem>
                <SelectItem value="textlocal">TextLocal (India)</SelectItem>
                <SelectItem value="fast2sms">Fast2SMS (India)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Twilio Settings */}
          {settings.sms_provider === 'twilio' && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-4">
              <h3 className="font-semibold text-red-900">Twilio Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('Account SID', 'twilio_account_sid', 'ACxxxxx')}
                {renderKeyInput('Auth Token', 'twilio_auth_token', 'Your Auth Token')}
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={settings.twilio_phone_number || ''}
                  onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                  placeholder="+1234567890"
                />
              </div>
              <p className="text-xs text-red-700">Get credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a></p>
            </div>
          )}

          {/* MSG91 Settings */}
          {settings.sms_provider === 'msg91' && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
              <h3 className="font-semibold text-blue-900">MSG91 Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('Auth Key', 'msg91_auth_key', 'Your MSG91 Auth Key')}
                <div>
                  <Label>Sender ID (6 chars)</Label>
                  <Input
                    value={settings.msg91_sender_id || ''}
                    onChange={(e) => setSettings({ ...settings, msg91_sender_id: e.target.value })}
                    placeholder="SCHOOL"
                    maxLength={6}
                  />
                </div>
              </div>
              <p className="text-xs text-blue-700">Get credentials from <a href="https://msg91.com" target="_blank" rel="noopener noreferrer" className="underline">MSG91 Dashboard</a></p>
            </div>
          )}

          {/* TextLocal Settings */}
          {settings.sms_provider === 'textlocal' && (
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
              <h3 className="font-semibold text-purple-900">TextLocal Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('API Key', 'textlocal_api_key', 'Your TextLocal API Key')}
                <div>
                  <Label>Sender Name</Label>
                  <Input
                    value={settings.textlocal_sender || ''}
                    onChange={(e) => setSettings({ ...settings, textlocal_sender: e.target.value })}
                    placeholder="SCHOOL"
                  />
                </div>
              </div>
              <p className="text-xs text-purple-700">Get credentials from <a href="https://textlocal.in" target="_blank" rel="noopener noreferrer" className="underline">TextLocal Dashboard</a></p>
            </div>
          )}

          {/* Fast2SMS Settings */}
          {settings.sms_provider === 'fast2sms' && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-4">
              <h3 className="font-semibold text-green-900">Fast2SMS Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                {renderKeyInput('API Key', 'fast2sms_api_key', 'Your Fast2SMS API Key')}
                <div>
                  <Label>Sender ID (Optional)</Label>
                  <Input
                    value={settings.fast2sms_sender_id || ''}
                    onChange={(e) => setSettings({ ...settings, fast2sms_sender_id: e.target.value })}
                    placeholder="FSTSMS"
                  />
                </div>
              </div>
              <p className="text-xs text-green-700">Get credentials from <a href="https://fast2sms.com" target="_blank" rel="noopener noreferrer" className="underline">Fast2SMS Dashboard</a></p>
            </div>
          )}

          {/* Notification Options */}
          <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
            <h3 className="font-semibold text-gray-800">Enable SMS Notifications For:</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_fee_reminder} onChange={(e) => setSettings({ ...settings, enable_fee_reminder: e.target.checked })} className="rounded" />
                <span className="text-sm">Fee Reminders</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_admission_notification} onChange={(e) => setSettings({ ...settings, enable_admission_notification: e.target.checked })} className="rounded" />
                <span className="text-sm">Admission Notifications</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={settings.enable_attendance_alert} onChange={(e) => setSettings({ ...settings, enable_attendance_alert: e.target.checked })} className="rounded" />
                <span className="text-sm">Attendance Alerts</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
            <input
              type="checkbox"
              id="sms_enabled"
              checked={settings.is_enabled}
              onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
              className="rounded"
            />
            <Label htmlFor="sms_enabled" className="cursor-pointer">Enable SMS Service</Label>
          </div>

          <div className="flex gap-3">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save SMS Settings'}
            </Button>
          </div>

          {/* Test SMS */}
          <div className="p-4 border-t pt-4">
            <h3 className="font-semibold mb-3">Test SMS</h3>
            <div className="flex gap-2">
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter phone number (e.g., 9876543210)"
                className="flex-1"
              />
              <Button onClick={handleTestSMS} disabled={testing} variant="outline">
                <Send className="w-4 h-4 mr-2" />
                {testing ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SMSSettings;
